import { Icon } from '@iconify/react';
import { Card, CardBody, cn, Spinner } from '@nextui-org/react';
import { useState } from 'react';

interface DiscoverCardProps {
  title: string;
  description?: string;
  bgColor?: string;
  hoverBgColor?: string;
  onAction?: () => void;
  isLoading?: boolean;
  className?: string;
}

/**
 * DiscoverCard - Card component for discover/search pages
 * Uses 0.75 opacity gradients (0.85 on hover)
 */
const DiscoverCard = ({
  title,
  description,
  bgColor,
  hoverBgColor,
  onAction,
  isLoading,
  className = '',
}: DiscoverCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={cn(
          'origin-center',
          'motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out',
          isHovered && 'motion-safe:-translate-y-[3px] motion-safe:scale-[1.02]',
          'motion-safe:active:scale-[0.99]'
        )}
      >
        <Card
          className={cn(
            'w-full md:w-[200px] aspect-square md:h-[200px] shrink-0 min-w-0 rounded-2xl border-0 transition-shadow duration-200',
            isHovered && 'shadow-lg',
            className
          )}
          shadow="sm"
          style={{
            background: isHovered && hoverBgColor ? hoverBgColor : bgColor,
          }}
          isPressable
          disableRipple
          disableAnimation
          onPress={onAction}
          aria-label={`${title} - ${description || 'Click to start'}`}
        >
          <CardBody className="p-4 md:p-5 flex flex-col justify-between h-full">
            <div className="flex flex-col gap-1.5">
              <h4 className="text-white font-medium text-sm md:text-base leading-tight line-clamp-4">
                {title}
              </h4>
            </div>

            <div className="flex items-end justify-end">
              {isLoading ? (
                <Spinner size="sm" color="white" />
              ) : (
                <div
                  aria-hidden
                  className={cn(
                    'bg-white/25 text-white rounded-full w-7 h-7 flex items-center justify-center',
                    isHovered && 'bg-white/35',
                    'motion-safe:transition-transform motion-safe:duration-200',
                    isHovered && 'motion-safe:translate-x-1'
                  )}
                >
                  <Icon icon="lucide:arrow-right" className="w-3.5 h-3.5" />
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default DiscoverCard;
